import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight, Building2, ClipboardList, CheckSquare, ScrollText, Cpu } from 'lucide-react';
import type { AuditEntry } from '@property/shared';
import { useAudits } from '../use-audits.js';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { cn } from '@/lib/utils';
import { formatTimestamp } from '@/lib/date';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type ActionCategory = 'property' | 'inspection' | 'task' | 'user' | 'system';

function categorize(action: string): ActionCategory {
  if (action.startsWith('property.')) return 'property';
  if (action.startsWith('inspection.')) return 'inspection';
  if (action.startsWith('task.')) return 'task';
  if (action.startsWith('user.') || action.startsWith('role.')) return 'user';
  return 'system';
}

const CATEGORY_STYLES: Record<ActionCategory, string> = {
  property: 'bg-blue-500/10 text-blue-600 ring-blue-500/20',
  inspection: 'bg-amber-500/10 text-amber-600 ring-amber-500/20',
  task: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/20',
  user: 'bg-violet-500/10 text-violet-600 ring-violet-500/20',
  system: 'bg-muted text-muted-foreground ring-border',
};

const ENTITY_ICONS: Record<string, React.ReactNode> = {
  property: <Building2 className="h-3 w-3" />,
  inspection: <ClipboardList className="h-3 w-3" />,
  task: <CheckSquare className="h-3 w-3" />,
};

const ENTITY_ROUTES: Record<string, (id: string) => string> = {
  property: (id) => `/dashboard/land-monitoring/${id}`,
  inspection: (id) => `/dashboard/inspection/${id}`,
  task: (id) => `/dashboard/task/${id}`,
};

// ── sub-components ─────────────────────────────────────────────────────────────

function ActionChip({ action }: { action: string }) {
  const cat = categorize(action);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 font-mono text-xs ring-1 ring-inset',
        CATEGORY_STYLES[cat],
      )}
    >
      {action}
    </span>
  );
}

function EntityLink({
  entityType,
  entityId,
}: {
  entityType: string | null;
  entityId: string | null;
}) {
  if (!entityType || !entityId) return <span className="text-muted-foreground">—</span>;

  const routeFn = ENTITY_ROUTES[entityType];
  const icon = ENTITY_ICONS[entityType];
  const label = `${entityId.slice(0, 8)}…`;

  if (!routeFn) {
    return (
      <span className="font-mono text-xs text-muted-foreground">
        {entityType}/{entityId}
      </span>
    );
  }

  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic entity routes
      to={routeFn(entityId) as any}
      className="inline-flex items-center gap-1.5 text-xs font-mono text-foreground underline-offset-2 hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {icon}
      {label}
    </Link>
  );
}

function UserCell({ entry }: { entry: AuditEntry }) {
  const isSystem = !entry.userId;

  if (isSystem) {
    return (
      <div className="flex items-center gap-2.5">
        <div className="h-7 w-7 shrink-0 rounded-full bg-muted flex items-center justify-center ring-1 ring-border/40">
          <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium leading-none">System</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">automated</p>
        </div>
      </div>
    );
  }

  const initials =
    entry.user?.name
      ?.split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('') ?? '?';

  return (
    <div className="flex items-center gap-2.5">
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarImage src={entry.user?.profilePictureUrl ?? undefined} />
        <AvatarFallback className="text-[10px] font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium leading-none">
          {entry.user?.name ?? (
            <span className="font-mono text-xs text-muted-foreground">{entry.userId}</span>
          )}
        </p>
        {entry.role && (
          <p className="mt-0.5 text-[11px] text-muted-foreground capitalize">{entry.role}</p>
        )}
      </div>
    </div>
  );
}

function ExpandedInputs({ inputs }: { inputs: unknown }) {
  const json = JSON.stringify(inputs, null, 2);
  return (
    <pre className="overflow-x-auto rounded-lg bg-muted/50 px-4 py-3 font-mono text-xs leading-relaxed text-muted-foreground ring-1 ring-inset ring-border/40">
      {json}
    </pre>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export function AuditLog() {
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data, total, isLoading, isFetching } = useAudits({
    viewPage: page,
    viewPageSize: PAGE_SIZE,
  });

  const toggleExpand = (id: number) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className={cn('space-y-4', isFetching && !isLoading && 'opacity-70 transition-opacity')}>
      <div className="rounded-xl border border-border/40 bg-background/50 backdrop-blur-md overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow className="hover:bg-transparent border-border/40">
                <TableHead className="py-4 w-8" />
                <TableHead className="py-4">User</TableHead>
                <TableHead className="py-4">Action</TableHead>
                <TableHead className="py-4">Entity</TableHead>
                <TableHead className="py-4 text-right tabular-nums">When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: PAGE_SIZE }).map((_, i) => (
                  <TableRow key={i} className="border-border/40">
                    {[1, 2, 3, 4, 5].map((j) => (
                      <TableCell key={j} className="py-4">
                        <Skeleton className="h-4 w-full rounded" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data.length === 0 ? (
                <TableRow className="border-border/40 hover:bg-transparent">
                  <TableCell colSpan={5} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted/50 mb-1">
                        <ScrollText className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-semibold text-foreground">No audit entries</p>
                      <p className="text-xs text-muted-foreground">
                        System actions will appear here as they occur.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data.flatMap((entry) => {
                  const isExpanded = expandedId === entry.id;
                  const hasInputs = entry.inputs != null;
                  return [
                    <TableRow
                      key={entry.id}
                      className={cn(
                        'border-border/40 transition-colors',
                        hasInputs && 'cursor-pointer hover:bg-primary/[0.03]',
                        isExpanded && 'bg-muted/20 border-b-0',
                      )}
                      onClick={() => hasInputs && toggleExpand(entry.id)}
                    >
                      <TableCell className="py-4 pr-0 w-8">
                        {hasInputs && (
                          <ChevronRight
                            className={cn(
                              'h-3.5 w-3.5 text-muted-foreground transition-transform duration-150',
                              isExpanded && 'rotate-90',
                            )}
                          />
                        )}
                      </TableCell>
                      <TableCell className="py-4">
                        <UserCell entry={entry} />
                      </TableCell>
                      <TableCell className="py-4">
                        <ActionChip action={entry.action} />
                      </TableCell>
                      <TableCell className="py-4">
                        <EntityLink entityType={entry.entityType} entityId={entry.entityId} />
                      </TableCell>
                      <TableCell className="py-4 text-right tabular-nums text-xs text-muted-foreground">
                        {formatTimestamp(entry.createdAt)}
                      </TableCell>
                    </TableRow>,
                    isExpanded && (
                      <TableRow
                        key={`${entry.id}-expanded`}
                        className="border-border/40 hover:bg-transparent"
                      >
                        <TableCell colSpan={5} className="pb-4 pt-0 pl-10 pr-6">
                          <ExpandedInputs inputs={entry.inputs} />
                        </TableCell>
                      </TableRow>
                    ),
                  ].filter(Boolean);
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {!isLoading && (
        <DataTablePagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
